import axios from "axios";
import { getStoredToken } from "../utils/tokenUtils";
import { getStoredClientId } from "../utils/tokenUtils";
import { injectClientIdIntoConfig } from "../utils/clientContext";
import { API_BASE } from "../config/api.config";
import { installApiLoadingInterceptors } from "../utils/apiLoading";

/**
 * AI Service client for generating test cases
 * Uses API_BASE.ai directly - the AI service uses /api/ai routes, not the prefixed /ai pattern
 */
class AIService {
  constructor() {
    this.client = axios.create({
      baseURL: API_BASE.ai,
      headers: {
        "Content-Type": "application/json",
      },
    });

    // Add auth token to requests
    this.client.interceptors.request.use(
      (config) => {
        const token = getStoredToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return injectClientIdIntoConfig(config);
      },
      (error) => Promise.reject(error),
    );

    installApiLoadingInterceptors(this.client);
  }

  /**
   * Generate test cases using AI
   * @param {Object} params - Generation parameters
   * @param {string} params.moduleName - Name of the module
   * @param {string} params.requirements - Requirements description
   * @param {string} params.mode - Generation mode: 'new' | 'regenerate' | 'more' | 'update'
   * @param {number} params.testCaseCount - Number of test cases to generate
   * @param {string} params.prdLink - PRD link (optional)
   * @param {string[]} params.jiraLinks - Jira ticket links (optional)
   * @param {string[]} params.figmaLinks - Figma design links (optional)
   * @param {string[]} params.sketchLinks - Sketch design links (optional)
   * @returns {Promise<Object>}
   */
  async generateTestCases({
    moduleName,
    requirements,
    mode = "new",
    testCaseCount = 5,
    prdLink,
    jiraLinks = [],
    figmaLinks = [],
    sketchLinks = [],
  }) {
    try {
      const clientId = getStoredClientId();
      const response = await this.client.post("/api/ai/generate-testcases", {
        moduleName,
        requirements,
        mode,
        testCaseCount,
        client_id: clientId,
        prdLink,
        jiraLinks,
        figmaLinks,
        sketchLinks,
      });
      return response.data;
    } catch (error) {
      console.error("AI Service Error:", error);
      throw error;
    }
  }

  /**
   * Get available AI modes
   * @returns {Promise<Object>}
   */
  async getAIModes() {
    try {
      const response = await this.client.get("/api/ai/modes");
      return response.data;
    } catch (error) {
      console.error("AI Service Error:", error);
      throw error;
    }
  }

  /**
   * AI Assistant chat (Web UI)
   * @param {Object} params
   * @param {{role:'system'|'user'|'assistant', content:string}[]} params.messages
   * @param {string=} params.systemPrompt
   */
  async chat({ messages, systemPrompt, content_type_id, chat_id }) {
    try {
      const body = { messages, systemPrompt };
      if (content_type_id) body.content_type_id = content_type_id;
      if (chat_id) body.chat_id = chat_id;
      const response = await this.client.post("/api/ai/chat", body);
      return response.data;
    } catch (error) {
      console.error("AI Service Chat Error:", error);
      throw error;
    }
  }
  async newChat({ messages, systemPrompt, content_type_id = "" }) {
    try {
      const response = await this.client.post("/api/ai/chat/new", {
        messages,
        systemPrompt,
        content_type_id,
      });
      return response?.data?.chat_id;
    } catch (error) {
      console.error("AI Service Chat Error:", error);
      return null;
    }
  }

  /**
   * Get all previous chats
   */
  async chats(params = {}) {
    try {
      const response = await this.client.get("/api/ai/chats", { params });
      return response.data;
    } catch (error) {
      console.error("AI Service Chats Error:", error);
      throw error;
    }
  }

  /**
   * Create a new chat session
   */
  async new(params = {}) {
    try {
      const response = await this.client.post("/api/ai/chat/new", params);
      return response.data;
    } catch (error) {
      console.error("AI Service New Chat Error:", error);
      throw error;
    }
  }

  /**
   * Get messages for a specific chat
   */
  async getChatMessages(chatId) {
    try {
      const response = await this.client.get(
        `/api/ai/chats/${encodeURIComponent(chatId)}/messages`,
      );
      return response.data;
    } catch (error) {
      console.error("AI Service Get Chat Messages Error:", error);
      throw error;
    }
  }
}

// Export singleton instance
export const aiService = new AIService();
